import { useEffect, useRef, useState } from "react";
import { Check, Copy, Link2, Zap } from "lucide-react";

import { StatusDot } from "@/presentation/components/status/status-dot";
import { DELIVERY_STATUS_LABEL, DELIVERY_STATUS_TONE } from "@/presentation/data/board";
import { fmtShort } from "@/presentation/data/planner";
import { cn } from "@monorepo-apps/ui/src/lib/utils";
import type { BoardFeatureRow } from "@/infrastructure/supabase/database.types";

interface PlannerTaskCardProps {
  feature: BoardFeatureRow;
  leverage: number;
  rank: number | null;
  todayMs: number;
  onOpen: () => void;
}

export function PlannerTaskCard({
  feature,
  leverage,
  rank,
  todayMs,
  onOpen,
}: PlannerTaskCardProps) {
  // Tap the code to copy it (same behaviour as the board's Item column).
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
    },
    []
  );
  const copyCode = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!feature.code) return;
    navigator.clipboard
      ?.writeText(feature.code)
      .then(() => {
        setCopied(true);
        if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
        copyTimer.current = window.setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {});
  };

  const dueMs = feature.due_date ? new Date(feature.due_date + "T00:00:00").getTime() : null;
  const overdue = feature.delivery_status !== "done" && dueMs !== null && todayMs > dueMs;
  const lateDays = overdue && dueMs !== null ? Math.round((todayMs - dueMs) / 86_400_000) : 0;
  const dueColor =
    feature.delivery_status === "done"
      ? "var(--rag-green)"
      : overdue
        ? "var(--rag-red)"
        : "var(--muted-foreground)";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="border-border/60 flex cursor-pointer items-start gap-2.5 border-t px-3.5 py-2 transition-colors first:border-t-0 hover:bg-[oklch(0.55_0.17_190_/_0.04)]"
    >
      {rank !== null ? (
        <span className="font-data text-muted-foreground w-4 shrink-0 pt-0.5 text-right text-[11px]">
          {rank}
        </span>
      ) : null}
      <span className="pt-1">
        <StatusDot tone={DELIVERY_STATUS_TONE[feature.delivery_status]} size="md" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] leading-tight">
          {feature.code ? (
            <button
              type="button"
              onClick={copyCode}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={copied ? "Copied" : `Copy code ${feature.code}`}
              title={copied ? "Copied!" : "Tap to copy code"}
              className={cn(
                "font-data group/code hover:bg-muted mr-1.5 inline-flex translate-y-px cursor-pointer items-center gap-0.5 rounded-[4px] px-1 py-px text-[10.5px] tabular-nums transition-colors",
                copied ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <span
                className={cn(
                  "underline decoration-dotted underline-offset-2",
                  copied
                    ? "decoration-transparent"
                    : "decoration-muted-foreground/40 group-hover/code:decoration-current"
                )}
              >
                {copied ? "copied" : feature.code}
              </span>
              {copied ? (
                <Check className="size-2.5 shrink-0" aria-hidden />
              ) : (
                <Copy
                  className="size-2.5 shrink-0 opacity-50 transition-opacity group-hover/code:opacity-100"
                  aria-hidden
                />
              )}
            </button>
          ) : null}
          {feature.mvp_blocking ? (
            <Zap className="mr-0.5 inline size-3 text-[var(--rag-red)]" aria-hidden />
          ) : null}
          {feature.title}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className="font-data inline-flex items-center gap-1 rounded-[5px] bg-[oklch(0.55_0.22_300_/_0.1)] px-1.5 py-0.5 text-[9.5px] text-[oklch(0.55_0.22_300)]">
            <Link2 className="size-2.5" /> unblocks {leverage}
          </span>
          <span
            className="font-data rounded-[5px] px-1.5 py-0.5 text-[9.5px]"
            style={{
              color: dueColor,
              border: `1px solid color-mix(in oklch, ${dueColor} 33%, transparent)`,
            }}
          >
            due {feature.due_date ? fmtShort(new Date(feature.due_date + "T00:00:00")) : "—"}
            {overdue ? ` · +${lateDays}d` : ""}
          </span>
          <span className="font-data text-muted-foreground rounded-[5px] px-1.5 py-0.5 text-[9.5px]">
            {DELIVERY_STATUS_LABEL[feature.delivery_status]}
          </span>
        </div>
      </div>
    </div>
  );
}
